import React, { useEffect, useState } from 'react';
import { statusClass, formatMana, totalPrizePool, compareTournaments, MEDALS } from './format.js';

function TournamentRow({ tournament, extra }) {
  return (
    <a className="user-tournament-row" href={`/tournament.html?id=${tournament.id}`}>
      <span className="row-title">{tournament.name}</span>
      <span className="user-row-right">
        {extra}
        <span className={`status-pill ${statusClass(tournament.status)}`}>{tournament.status}</span>
      </span>
    </a>
  );
}

export default function UserPage({ username: initialUsername }) {
  const [query, setQuery] = useState(initialUsername || '');
  const [username, setUsername] = useState(initialUsername || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    setError('');
    setData(null);

    fetch(`/api/tournaments/user/${encodeURIComponent(username)}`)
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to look up user');
        }
        return r.json();
      })
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [username]);

  const search = () => {
    const name = query.trim().replace(/^@/, '');
    if (!name) return;
    setUsername(name);
    const url = new URL(window.location.href);
    url.searchParams.set('u', name);
    window.history.replaceState({}, '', url);
  };

  const wins = data ? data.podiums.filter(p => p.rank === 1).length : 0;
  const podiumCount = data ? data.podiums.length : 0;
  const totalWinnings = data
    ? data.podiums.reduce((sum, p) => sum + (Number(p.prize) || 0), 0)
    : 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">User Lookup</h1>
        <p className="page-subtitle">See any Manifold user's tournament wins and hosting history</p>
      </div>

      <div className="user-search-row">
        <input
          type="text"
          className="search-bar"
          placeholder="Manifold username, e.g. Austin"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
        />
        <button className="btn btn-primary" onClick={search} disabled={!query.trim()}>
          Look up
        </button>
      </div>

      {loading && <div className="loading">Looking up {username}...</div>}
      {error && <div className="form-error-message">{error}</div>}

      {!loading && !error && !data && (
        <div className="card">
          <div className="center-message">
            Enter a Manifold username to see their tournament record.
          </div>
        </div>
      )}

      {data && !loading && (
        <>
          <div className="card user-profile-card">
            {data.user.avatarUrl && <img src={data.user.avatarUrl} alt="" className="avatar avatar-lg" />}
            <div>
              <div className="user-profile-name">{data.user.userName}</div>
              <a
                href={data.user.userUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="host-link user-profile-handle"
              >
                @{data.user.username} on Manifold ↗
              </a>
            </div>
          </div>

          <div className="achievements-grid">
            <div className="achievement-tile">
              <div className="achievement-icon">🏆</div>
              <div className="achievement-label">Tournament Wins</div>
              <div className="achievement-value">{wins}</div>
            </div>
            <div className="achievement-tile">
              <div className="achievement-icon">🥉</div>
              <div className="achievement-label">Podium Finishes</div>
              <div className="achievement-value">{podiumCount}</div>
            </div>
            <div className="achievement-tile">
              <div className="achievement-icon">💰</div>
              <div className="achievement-label">Prize Winnings</div>
              <div className="achievement-value">{totalWinnings > 0 ? formatMana(totalWinnings) : 'Ṁ0'}</div>
            </div>
          </div>

          <div className="user-page-grid">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Podium Finishes</h3>
              </div>
              {data.podiums.length === 0 ? (
                <div className="center-message">
                  No podium finishes yet — only finished tournaments count.
                </div>
              ) : (
                <div className="user-tournament-list">
                  {data.podiums.map(p => (
                    <TournamentRow
                      key={`${p.id}-${p.rank}`}
                      tournament={p}
                      extra={
                        <span className="podium-detail">
                          {MEDALS[p.rank] || `#${p.rank}`}
                          {p.prize ? ` ${formatMana(p.prize)}` : ''}
                        </span>
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Tournaments Hosted</h3>
              </div>
              {data.hosted.length === 0 ? (
                <div className="center-message">No tournaments hosted yet.</div>
              ) : (
                <div className="user-tournament-list">
                  {[...data.hosted].sort(compareTournaments).map(t => {
                    const pool = totalPrizePool(t.prizes);
                    return (
                      <TournamentRow
                        key={t.id}
                        tournament={t}
                        extra={pool > 0 ? <span className="prize-pool">{formatMana(pool)}</span> : null}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
